import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AppState } from '../index';
import { StripeActions } from './stripe.actions';
import * as StripeSelectors from './stripe.selectors';
import {
  UserSubscription,
  SubscriptionPlansResponse,
  SecondsProductsResponse,
  SessionCheckResponse
} from '@app/core/api/stripe';
import { PaymentProduct } from '@app/core/infrastructure/payment';
import { StripeUsageCalculator } from '@app/core/infrastructure/stripe';

/**
 * Stripe Facade Service
 * 
 * Provides a clean, simplified API for components to interact with Stripe state.
 * Components should use this facade instead of directly dispatching actions or selecting state.
 */
@Injectable({
  providedIn: 'root'
})
export class StripeFacade {
  
  // Observables for components to subscribe to
  readonly subscriptionPlans$: Observable<SubscriptionPlansResponse | null>;
  readonly subscriptionPlansLoading$: Observable<boolean>;
  readonly secondsProducts$: Observable<SecondsProductsResponse | null>;
  readonly secondsProductsLoading$: Observable<boolean>;
  
  readonly userSubscription$: Observable<UserSubscription | null>;
  readonly userSubscriptionLoading$: Observable<boolean>;
  readonly realtimeSubscription$: Observable<UserSubscription | null>;
  
  /**
   * Current subscription with real-time updates during active sessions.
   * This selector merges the base subscription with real-time session data,
   * providing the most up-to-date subscription information.
   * Use this instead of manually combining userSubscription$ and realtimeSubscription$.
   */
  readonly currentSubscription$: Observable<UserSubscription | null>;
  
  readonly isSessionActive$: Observable<boolean>;
  readonly sessionUsageSeconds$: Observable<number>;
  readonly sessionUsageMinutes$: Observable<number>;
  readonly realtimeRemainingSeconds$: Observable<number>;
  readonly realtimeRemainingMinutes$: Observable<number>;
  
  readonly canUseService$: Observable<boolean>;
  readonly hasActiveSubscription$: Observable<boolean>;
  readonly usagePercentage$: Observable<number>;
  readonly remainingMinutes$: Observable<number>;
  
  readonly shouldShowLowMinutesWarning$: Observable<boolean>;
  readonly shouldShowCriticalWarning$: Observable<boolean>;
  readonly lowMinutesWarningText$: Observable<string>;
  
  readonly checkoutLoading$: Observable<boolean>;
  readonly checkoutError$: Observable<string | null>;
  readonly portalLoading$: Observable<boolean>;
  readonly isCheckoutOrPortalLoading$: Observable<boolean>;

  // Activate subscription (trial)
  readonly activateSubscriptionLoading$: Observable<boolean>;
  readonly activateSubscriptionError$: Observable<string | null>;
  readonly isTrial$: Observable<boolean>;
  readonly isTrialLimitReached$: Observable<boolean>;

  // Payment source — determines which billing UI to show
  readonly paymentSource$: Observable<'stripe' | 'apple' | null>;
  readonly isAppleSubscriber$: Observable<boolean>;
  readonly isStripeSubscriber$: Observable<boolean>;

  // IAP (In-App Purchase)
  readonly iapProducts$: Observable<PaymentProduct[]>;
  readonly iapProductsLoading$: Observable<boolean>;
  readonly iapPurchaseLoading$: Observable<boolean>;
  readonly iapPurchaseError$: Observable<string | null>;
  readonly iapRestoreLoading$: Observable<boolean>;

  constructor(
    private store: Store<AppState>,
    private usageCalculator: StripeUsageCalculator
  ) {
    // Initialize all selectors
    this.subscriptionPlans$ = this.store.select(StripeSelectors.selectSubscriptionPlans);
    this.subscriptionPlansLoading$ = this.store.select(StripeSelectors.selectSubscriptionPlansLoading);
    this.secondsProducts$ = this.store.select(StripeSelectors.selectSecondsProducts);
    this.secondsProductsLoading$ = this.store.select(StripeSelectors.selectSecondsProductsLoading);
    
    this.userSubscription$ = this.store.select(StripeSelectors.selectUserSubscription);
    this.userSubscriptionLoading$ = this.store.select(StripeSelectors.selectUserSubscriptionLoading);
    this.realtimeSubscription$ = this.store.select(StripeSelectors.selectRealtimeSubscription);
    
    // Combine base subscription with real-time updates
    // During active sessions, realtimeSubscription$ will have updated usage data
    // Otherwise, fall back to base userSubscription$
    this.currentSubscription$ = combineLatest([
      this.userSubscription$,
      this.realtimeSubscription$
    ]).pipe(
      map(([base, realtime]) => realtime || base),
      shareReplay(1)
    );
    
    this.isSessionActive$ = this.store.select(StripeSelectors.selectIsSessionActive);
    this.sessionUsageSeconds$ = this.store.select(StripeSelectors.selectSessionUsageSeconds);
    this.sessionUsageMinutes$ = this.store.select(StripeSelectors.selectSessionUsageMinutes);
    this.realtimeRemainingSeconds$ = this.store.select(StripeSelectors.selectRealtimeRemainingSeconds);
    this.realtimeRemainingMinutes$ = this.store.select(StripeSelectors.selectRealtimeRemainingMinutes);
    
    this.canUseService$ = this.store.select(StripeSelectors.selectCanUseService);
    this.hasActiveSubscription$ = this.store.select(StripeSelectors.selectHasActiveSubscription);
    this.usagePercentage$ = this.store.select(StripeSelectors.selectUsagePercentage);
    this.remainingMinutes$ = this.store.select(StripeSelectors.selectRemainingMinutes);
    
    this.shouldShowLowMinutesWarning$ = this.store.select(StripeSelectors.selectShouldShowLowMinutesWarning);
    this.shouldShowCriticalWarning$ = this.store.select(StripeSelectors.selectShouldShowCriticalWarning);
    this.lowMinutesWarningText$ = this.remainingMinutes$.pipe(
      map(minutes => this.usageCalculator.getLowMinutesWarningText(minutes))
    );
    
    this.checkoutLoading$ = this.store.select(StripeSelectors.selectCheckoutLoading);
    this.checkoutError$ = this.store.select(StripeSelectors.selectCheckoutError);
    this.portalLoading$ = this.store.select(StripeSelectors.selectPortalLoading);
    
    // Combined loading for any checkout or portal operation
    this.isCheckoutOrPortalLoading$ = this.store.select(StripeSelectors.selectIsCheckoutOrPortalLoading);

    // Activate subscription
    this.activateSubscriptionLoading$ = this.store.select(StripeSelectors.selectActivateSubscriptionLoading);
    this.activateSubscriptionError$ = this.store.select(StripeSelectors.selectActivateSubscriptionError);
    this.isTrial$ = this.store.select(StripeSelectors.selectIsTrial);
    this.isTrialLimitReached$ = this.store.select(StripeSelectors.selectIsTrialLimitReached);

    // Payment source
    this.paymentSource$ = this.store.select(StripeSelectors.selectPaymentSource);
    this.isAppleSubscriber$ = this.store.select(StripeSelectors.selectIsAppleSubscriber);
    this.isStripeSubscriber$ = this.store.select(StripeSelectors.selectIsStripeSubscriber);

    // IAP
    this.iapProducts$ = this.store.select(StripeSelectors.selectIapProducts);
    this.iapProductsLoading$ = this.store.select(StripeSelectors.selectIapProductsLoading);
    this.iapPurchaseLoading$ = this.store.select(StripeSelectors.selectIapPurchaseLoading);
    this.iapPurchaseError$ = this.store.select(StripeSelectors.selectIapPurchaseError);
    this.iapRestoreLoading$ = this.store.select(StripeSelectors.selectIapRestoreLoading);
  }

  // ==================== Public API Methods ====================

  /**
   * Load all plans - subscriptions and seconds products in one call
   * Uses the unified /public/stripe/plans endpoint
   */
  loadAllPlans(): void {
    this.store.dispatch(StripeActions.loadAllPlans());
  }

  /**
   * Load user subscription (protected endpoint)
   */
  loadUserSubscription(): void {
    this.store.dispatch(StripeActions.loadUserSubscription());
  }

  /**
   * Start auto-refresh subscription (every 60 seconds)
   */
  startAutoRefresh(): void {
    this.store.dispatch(StripeActions.startAutoRefresh());
  }

  /**
   * Stop auto-refresh subscription
   */
  stopAutoRefresh(): void {
    this.store.dispatch(StripeActions.stopAutoRefresh());
  }

  /**
   * Create subscription checkout session
   * @param priceId - Stripe price ID for the subscription plan
   * @param promoCode - Optional promotional code for trial/discount
   */
  createSubscriptionCheckout(priceId: string, promoCode?: string): void {
    this.store.dispatch(StripeActions.createSubscriptionCheckout({ priceId, promoCode }));
  }

  /**
   * Create seconds purchase checkout session
   * @param priceId - Stripe price ID from seconds_products (e.g., "price_1S45oj...")
   */
  createSecondsCheckout(priceId: string): void {
    this.store.dispatch(StripeActions.createSecondsCheckout({ priceId }));
  }

    /**
   * Get customer portal URL and open in browser
   */
  getCustomerPortal(targetPriceId?: string): void {
    this.store.dispatch(StripeActions.getCustomerPortal({ targetPriceId }));
  }

  /**
   * Activate subscription (end trial early)
   * Triggers immediate payment and converts trial to active subscription
   */
  activateSubscription(): void {
    this.store.dispatch(StripeActions.activateSubscription());
  }

  /**
   * Clear external navigation loading states (for browser dismissal)
   */
  clearExternalNavigationLoadingStates(): void {
    this.store.dispatch(StripeActions.clearExternalNavigationLoadingStates());
  }

  // ==================== IAP (In-App Purchase) Methods ====================

  /**
   * Load IAP products from the native store (iOS App Store)
   */
  iapLoadProducts(): void {
    this.store.dispatch(StripeActions.iapLoadProducts());
  }

  /**
   * Purchase a product via native IAP
   * @param productId - Apple product ID (e.g., 'my.greatday.sub.monthly.credits.starter')
   */
  iapPurchase(productId: string): void {
    this.store.dispatch(StripeActions.iapPurchase({ productId }));
  }

  /**
   * Restore previous IAP purchases (Apple requirement)
   */
  iapRestore(): void {
    this.store.dispatch(StripeActions.iapRestore());
  }

  /**
   * Check if user can start a session
   */
  async checkSessionAvailability(): Promise<SessionCheckResponse> {
    this.store.dispatch(StripeActions.checkSessionAvailability());
    
    // Wait for response
    const response = await firstValueFrom(
      this.store.select(StripeSelectors.selectSessionCheckResponse)
    );
    
    return response || {
      can_start_session: false,
      reason: 'Unable to validate session availability',
      remaining_seconds: 0
    };
  }

  /**
   * Start session tracking
   */
  startSessionTracking(initialRemainingSeconds?: number): void {
    this.store.dispatch(StripeActions.startSessionTracking({ initialRemainingSeconds }));
  }

  /**
   * Stop session tracking
   */
  stopSessionTracking(): void {
    this.store.dispatch(StripeActions.stopSessionTracking());
  }

  /**
   * Set authentication state (called by auth service/effects)
   */
  setAuthenticationState(isAuthenticated: boolean): void {
    this.store.dispatch(StripeActions.setAuthenticationState({ isAuthenticated }));
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.store.dispatch(StripeActions.clearCache());
  }

  // ==================== Helper Methods ====================

  /**
   * Get usage percentage for a subscription
   */
  getUsagePercentage(subscription: UserSubscription): number {
    return this.usageCalculator.calculateUsagePercentage(subscription);
  }

  /**
   * Get remaining minutes for a subscription
   */
  getRemainingMinutes(subscription: UserSubscription): number {
    return this.usageCalculator.getRemainingMinutes(subscription);
  }

  /**
   * Check if user can use service
   */
  canUseService(subscription: UserSubscription): boolean {
    return subscription?.can_use_service || false;
  }

  /**
   * Check if should show upgrade prompt
   */
  shouldShowUpgradePrompt(subscription: UserSubscription, threshold: number = 80): boolean {
    const percentage = this.usageCalculator.calculateUsagePercentage(subscription);
    return percentage >= threshold;
  }

  /**
   * Validate session start (async)
   */
  async validateSessionStart(): Promise<SessionCheckResponse> {
    return this.checkSessionAvailability();
  }

  /**
   * Get low minutes warning text
   */
  getLowMinutesWarningText(remainingMinutes: number, subscription?: UserSubscription): string {
    return this.usageCalculator.getLowMinutesWarningText(remainingMinutes, subscription);
  }

  /**
   * Check if should show low minutes warning
   */
  shouldShowLowMinutesWarning(subscription: UserSubscription, remainingMinutes: number): boolean {
    return this.usageCalculator.shouldShowLowMinutesWarning(subscription, remainingMinutes);
  }

  /**
   * Check if should show critical warning
   */
  shouldShowCriticalWarning(subscription: UserSubscription, remainingMinutes: number): boolean {
    return this.usageCalculator.shouldShowCriticalWarning(subscription, remainingMinutes);
  }
}
